import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const STAT_CONFIGS = [
  { value: '2', icon: 'ri-bus-2-line', labelKey: 'about_stat_buses' },
  { value: '57', icon: 'ri-armchair-line', labelKey: 'about_stat_seats' },
  { value: '100%', icon: 'ri-calendar-check-line', labelKey: 'about_stat_service' },
  { value: '3.5h', icon: 'ri-map-pin-2-line', labelKey: 'about_stat_route' },
];

export default function AboutSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section
      id="about"
      ref={ref as React.RefObject<HTMLElement>}
      className={`w-full bg-[#F8F5F0] py-24 lg:py-32 overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row items-start gap-16 lg:gap-24">
          {/* Left Column */}
          <div className="w-full lg:w-5/12">
            <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              {t('about_eyebrow')}
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] leading-tight mb-8">
              {t('about_h2_line1')}<br />
              <span className="text-[#C9A84C]">{t('about_h2_line2')}</span><br />
              {t('about_h2_line3')}
            </h2>
            <div className="space-y-5 text-[#555] text-base leading-relaxed">
              <p dangerouslySetInnerHTML={{ __html: t('about_p1') }} />
              <p>{t('about_p2')}</p>
              <p>{t('about_p3')}</p>
            </div>

            {/* Decorative gold line */}
            <div className="mt-10 flex items-center gap-4">
              <div className="h-px w-16 bg-[#C9A84C]" />
              <p className="text-[#C9A84C] text-sm font-semibold tracking-wider italic font-playfair">
                {t('about_motto')}
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="w-full lg:w-7/12">
            <div className="relative">
              <div className="w-full h-80 md:h-[420px] rounded-2xl overflow-hidden">
                <img
                  src="https://storage.readdy-site.link/project_files/543a30f0-c088-40ca-b55f-d366a28be6b3/9fcebef9-8307-45cb-9682-4175a357b369_Gemini_Generated_Image_3bti9v3bti9v3bti.png?v=1d701df96299e754ecc7aa9d264b084a"
                  alt="Phoenix Transportation luxury interior"
                  className="w-full h-full object-cover object-center"
                />
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                {STAT_CONFIGS.map((stat) => (
                  <div
                    key={stat.labelKey}
                    className="bg-white rounded-xl p-5 text-center border border-[#e8e4dc] hover:border-[#C9A84C]/40 transition-colors duration-300"
                  >
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                      <i className={`${stat.icon} text-[#C9A84C] text-2xl`} />
                    </div>
                    <p className="text-[#C9A84C] text-2xl font-bold font-playfair">{stat.value}</p>
                    <p className="text-[#888] text-xs mt-1 tracking-wide">{t(stat.labelKey)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
